import { TFunction } from 'i18next';
import {
  Operation,
  OperationDirection,
  RefundDetails,
} from './Transactions.types';
import { OPERATION_DIRECTION } from './Transactions.constants';
import {
  GetCurrentUserPaymentsQuery,
  GetCurrentUserTransactionsQuery,
  PaymentAd,
  UserPayment,
  UserTransaction,
} from './api';
import { formatDate, formatNumber } from '@/utils/helpers';
import { getPaymentMethodLabel } from '@/components/Payment/PaymentMethods.helpers';
import { PaymentMethod } from '@/api/types/payments.types';

const REFUND_SERVICE_ID = 'refund';

function getOperationDirection(amount: number): OperationDirection {
  return amount >= 0 ? OPERATION_DIRECTION.INCOME : OPERATION_DIRECTION.EXPENSE;
}

export function formatOperationAmount(
  amount: number,
  direction: OperationDirection,
  currency: string
): string {
  const sign = direction === OPERATION_DIRECTION.INCOME ? '' : '−';
  return `${sign}${formatNumber(amount, 2)} ${currency}`;
}

function resolvePaymentMethodLabel(
  t: TFunction,
  paymentMethod?: string | null
): string | undefined {
  if (!paymentMethod) return undefined;

  const normalized = paymentMethod.toUpperCase() as PaymentMethod;
  if (normalized === PaymentMethod.Reversal) return 'Reversal';
  if (normalized === PaymentMethod.Reimbursement) return 'Reimbursement';

  return getPaymentMethodLabel(t, normalized, undefined, true) || paymentMethod;
}

function mapAd(ad: PaymentAd | null) {
  if (!ad) {
    return undefined;
  }

  return {
    id: ad.id,
    legacyResourceId: ad.legacyResourceId,
    title: ad.title,
    path: ad.path,
    price: ad.price,
    image: ad?.photo?.url ?? null,
  };
}

function getNodeId(node: {
  paymentLegacyId?: number | null;
  serviceId: string;
  date: string;
  amount: number;
}): string {
  return (
    node.paymentLegacyId?.toString() ??
    `${node.serviceId}-${node.date}-${node.amount}`
  );
}

function buildOperation(
  base: {
    paymentLegacyId?: number | null;
    serviceId: string;
    displayServiceName: string;
    amount: number;
    paymentMethod?: string | null;
    date: string;
    isRefund?: boolean;
    refund?: RefundDetails;
  },
  direction: OperationDirection,
  ad: PaymentAd | null,
  t: TFunction
): Operation {
  const operationNumber = base.paymentLegacyId?.toString() ?? '-';
  const absAmount = Math.abs(base.amount);
  const paymentMethodLabel = resolvePaymentMethodLabel(t, base.paymentMethod);

  return {
    id: getNodeId({
      paymentLegacyId: base.paymentLegacyId,
      serviceId: base.serviceId,
      date: base.date,
      amount: base.amount,
    }),
    serviceId: base.serviceId,
    operationNumber,
    serviceName: base.displayServiceName,
    amount: absAmount,
    formattedAmount: formatOperationAmount(absAmount, direction, '₼'),
    paymentMethod: base.paymentMethod ?? undefined,
    paymentMethodLabel,
    date: formatDate(base.date, 'dd.MM.yyyy, HH:mm'),
    direction,
    isIncome: direction === OPERATION_DIRECTION.INCOME,
    isRefund: base.isRefund ?? false,
    ad: mapAd(ad),
    refund: base.refund,
  };
}

function mapRefundDetails(
  payment: UserPayment,
  t: TFunction
): RefundDetails | undefined {
  const original = payment.originalPayment;
  if (!original) return undefined;

  return {
    paidServiceName: original.displayServiceName,
    paidPaymentMethodLabel: resolvePaymentMethodLabel(
      t,
      original.paymentMethod
    ),
    adNumber: original.ad?.legacyResourceId?.toString(),
    rrn: original.rrn ?? undefined,
  };
}

export function mapPaymentToOperation(
  payment: UserPayment,
  t: TFunction
): Operation {
  const isRefund = payment.serviceId === REFUND_SERVICE_ID;
  const direction = isRefund
    ? OPERATION_DIRECTION.INCOME
    : OPERATION_DIRECTION.EXPENSE;

  return buildOperation(
    {
      paymentLegacyId: payment.paymentLegacyId,
      serviceId: payment.serviceId,
      displayServiceName: payment.displayServiceName,
      amount: payment.amount,
      paymentMethod: payment.paymentMethod,
      date: payment.updatedAt,
      isRefund,
      refund: mapRefundDetails(payment, t),
    },
    direction,
    payment.ad,
    t
  );
}

export function mapTransactionToOperation(
  transaction: UserTransaction,
  t: TFunction
): Operation {
  const direction = getOperationDirection(transaction.amount);

  return buildOperation(
    {
      paymentLegacyId: transaction.paymentLegacyId,
      serviceId: transaction.serviceId,
      displayServiceName: transaction.displayServiceName,
      amount: transaction.amount,
      paymentMethod: transaction.paymentMethod,
      date: transaction.createdAt,
    },
    direction,
    transaction.ad,
    t
  );
}

function mergeNodes<T>(
  prevNodes: T[],
  newNodes: T[],
  getNodeIdFn: (node: T) => string
): T[] {
  const existingIds = new Set(prevNodes.map(getNodeIdFn));
  const uniqueNewNodes = newNodes.filter(
    node => !existingIds.has(getNodeIdFn(node))
  );

  return [...prevNodes, ...uniqueNewNodes];
}

export function mergeUserPaymentsResults(
  prevResult: GetCurrentUserPaymentsQuery,
  { fetchMoreResult }: { fetchMoreResult: GetCurrentUserPaymentsQuery }
): GetCurrentUserPaymentsQuery {
  const prevPayments = prevResult.currentUserPayments;
  const newPayments = fetchMoreResult.currentUserPayments;
  const newList = newPayments?.nodes ?? [];

  if (!prevPayments || !newPayments || newList.length === 0) {
    return prevResult;
  }

  return {
    ...prevResult,
    currentUserPayments: {
      ...prevPayments,
      nodes: mergeNodes(prevPayments.nodes, newList, payment =>
        getNodeId({
          paymentLegacyId: payment.paymentLegacyId,
          serviceId: payment.serviceId,
          date: payment.updatedAt,
          amount: payment.amount,
        })
      ),
      pageInfo: newPayments.pageInfo,
      totalCount: newPayments.totalCount,
    },
  };
}

export function mergeUserTransactionsResults(
  prevResult: GetCurrentUserTransactionsQuery,
  { fetchMoreResult }: { fetchMoreResult: GetCurrentUserTransactionsQuery }
): GetCurrentUserTransactionsQuery {
  const prevTransactions = prevResult.currentUserTransactions;
  const newTransactions = fetchMoreResult.currentUserTransactions;
  const newList = newTransactions?.nodes ?? [];

  if (!prevTransactions || !newTransactions || newList.length === 0) {
    return prevResult;
  }

  return {
    ...prevResult,
    currentUserTransactions: {
      ...prevTransactions,
      nodes: mergeNodes(prevTransactions.nodes, newList, transaction =>
        getNodeId({
          paymentLegacyId: transaction.paymentLegacyId,
          serviceId: transaction.serviceId,
          date: transaction.createdAt,
          amount: transaction.amount,
        })
      ),
      pageInfo: newTransactions.pageInfo,
      totalCount: newTransactions.totalCount,
    },
  };
}
